import { AlertTriangle } from 'lucide-react';
import { AlertDialog } from 'radix-ui';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Input } from './input';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
  /** Require typing this text before confirming (destructive actions). */
  typeToConfirm?: string;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    setTyped('');
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  const blocked = Boolean(options?.typeToConfirm && typed.trim() !== options.typeToConfirm);
  const danger = options?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog.Root open={options !== null} onOpenChange={(open) => !open && close(false)}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-[60] bg-slate-950/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in dark:bg-black/60" />
          <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center p-4">
            <AlertDialog.Content className="pointer-events-auto w-full max-w-md rounded-2xl border border-line bg-elevated p-6 shadow-pop data-[state=open]:animate-pop-in">
              <div className="flex gap-4">
                <div
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-full',
                    danger
                      ? 'bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300'
                      : 'bg-brand-100 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300',
                  )}
                >
                  <AlertTriangle className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <AlertDialog.Title className="font-display text-base font-semibold text-ink">
                    {options?.title}
                  </AlertDialog.Title>
                  <AlertDialog.Description asChild>
                    <div className="mt-1.5 text-[13px] leading-relaxed text-ink-muted">
                      {options?.description ?? 'Are you sure you want to continue?'}
                    </div>
                  </AlertDialog.Description>
                  {options?.typeToConfirm ? (
                    <div className="mt-4">
                      <label className="text-xs text-ink-muted" htmlFor="confirm-type">
                        Type <span className="font-semibold text-ink">{options.typeToConfirm}</span> to
                        confirm
                      </label>
                      <Input
                        id="confirm-type"
                        className="mt-1.5"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <AlertDialog.Cancel asChild>
                  <Button variant="outline">{options?.cancelLabel ?? 'Cancel'}</Button>
                </AlertDialog.Cancel>
                <Button
                  variant={danger ? 'danger' : 'primary'}
                  disabled={blocked}
                  onClick={() => close(true)}
                >
                  {options?.confirmLabel ?? 'Confirm'}
                </Button>
              </div>
            </AlertDialog.Content>
          </div>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside ConfirmProvider');
  return confirm;
}
